import {
  AuthDivider,
  AuthFooterLink,
  AuthMessage,
  AuthPasswordInput,
  AuthSubmitButton,
  Button,
  GoogleAuthButton,
} from '@protoss/ui';
import { verifyPasswordResetCode } from 'firebase/auth';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthCard, AuthHeader, AuthLayout } from '../../components/auth';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseAuth } from '../../lib/firebase';
import { getPreAuthConfig } from '../../lib/preAuthConfig';
import './auth-pages.css';

type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

interface InviteSummary {
  inviteId: string;
  email: string;
  workspaceName: string;
  workspaceRole: 'workspace_admin' | 'workspace_member';
  moduleKeys: string[];
  status: InviteStatus;
  expiresAt: string;
}

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

type PageState = 'loading' | 'ready' | 'success' | 'error';

function mapPasswordResetError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (
    message.includes('auth/expired-action-code') ||
    message.includes('auth/invalid-action-code')
  ) {
    return 'This invitation link is invalid or has expired. Ask your workspace admin to send a new invite.';
  }
  return 'Unable to validate this invitation link. Ask your workspace admin to send a new invite.';
}

export function SetPasswordPage() {
  const { resetPassword, signInWithGoogle, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const showGoogleAuth = getPreAuthConfig().allowedMethods.includes('google');

  const continueUrl = searchParams.get('continueUrl');
  const inviteId = React.useMemo(() => {
    const directInviteId = searchParams.get('inviteId');
    if (directInviteId) return directInviteId;
    if (!continueUrl) return '';
    try {
      return new URL(continueUrl).searchParams.get('inviteId') ?? '';
    } catch {
      return '';
    }
  }, [continueUrl, searchParams]);
  const oobCode = searchParams.get('oobCode') ?? '';

  const [pageState, setPageState] = React.useState<PageState>('loading');
  const [invite, setInvite] = React.useState<InviteSummary | null>(null);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const validateInvite = async () => {
      if (!inviteId || !oobCode) {
        setGeneralError(
          'This invitation link is incomplete. Ask your workspace admin to send a new invite.'
        );
        setPageState('error');
        return;
      }

      try {
        const response = await fetch(`/api/auth/invites/${encodeURIComponent(inviteId)}`);
        if (!response.ok) {
          throw new Error('INVITE_NOT_FOUND');
        }

        const data = (await response.json()) as { invite: InviteSummary };
        if (cancelled) return;

        setInvite(data.invite);

        if (data.invite.status === 'accepted') {
          setEmail(data.invite.email);
          setGeneralError('This invitation has already been accepted. Sign in to continue.');
          setPageState('error');
          return;
        }

        if (data.invite.status === 'expired' || data.invite.status === 'revoked') {
          setEmail(data.invite.email);
          setGeneralError(
            'This invitation is no longer active. Ask your workspace admin to send a new invite.'
          );
          setPageState('error');
          return;
        }

        const resolvedEmail = await verifyPasswordResetCode(firebaseAuth, oobCode);
        if (cancelled) return;

        if (resolvedEmail.toLowerCase() !== data.invite.email.toLowerCase()) {
          throw new Error('INVITE_EMAIL_MISMATCH');
        }

        setEmail(resolvedEmail);
        setPageState('ready');
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'UNKNOWN_ERROR';
        if (message === 'INVITE_NOT_FOUND') {
          setGeneralError(
            'This invitation link is invalid. Ask your workspace admin to send a new invite.'
          );
        } else if (message === 'INVITE_EMAIL_MISMATCH') {
          setGeneralError(
            'This invitation link does not match the invited account. Ask your workspace admin to resend the invite.'
          );
        } else {
          setGeneralError(mapPasswordResetError(error));
        }
        setPageState('error');
      }
    };

    void validateInvite();

    return () => {
      cancelled = true;
    };
  }, [inviteId, oobCode]);

  const validateForm = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!password) {
      nextErrors.password = 'Password is required';
    } else if (password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const signInHref = `/signin?inviteId=${encodeURIComponent(inviteId)}&email=${encodeURIComponent(email)}`;

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setGeneralError(null);
    try {
      await signInWithGoogle();
      window.location.assign('/');
    } catch (error) {
      await signOut().catch(() => undefined);
      setIsGoogleLoading(false);
      const message = error instanceof Error ? error.message : 'UNKNOWN_ERROR';
      setGeneralError(
        message === 'NO_WORKSPACE_ACCESS'
          ? 'This Google account does not match the invited email address. Use the correct Google account.'
          : 'Unable to connect with Google. Please try again.'
      );
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGeneralError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(oobCode, password);
      setPageState('success');
    } catch (error) {
      setGeneralError(mapPasswordResetError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader title="Set your password" subtitle="Validating your invitation link…" />
          <div className="auth-form-container">
            <AuthMessage variant="info">Please wait while we validate your invitation.</AuthMessage>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  if (pageState === 'success') {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader
            title="Password set"
            subtitle={
              invite ? `Sign in to finish joining ${invite.workspaceName}.` : 'Sign in to continue.'
            }
          />
          <div className="auth-form-container">
            <AuthMessage variant="success">
              Your password has been saved. Sign in with {email} to complete your workspace access.
            </AuthMessage>
            <div className="auth-form">
              <Button
                variant="primary"
                size="md"
                onClick={() => window.location.assign(signInHref)}
              >
                Continue to sign in
              </Button>
            </div>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  if (pageState === 'error') {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthHeader
            title="Invitation unavailable"
            subtitle={invite ? `Workspace: ${invite.workspaceName}` : undefined}
          />
          <div className="auth-form-container">
            {generalError && <AuthMessage variant="error">{generalError}</AuthMessage>}
            {email ? (
              <div className="auth-form">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => window.location.assign(signInHref)}
                >
                  Go to sign in
                </Button>
              </div>
            ) : null}
          </div>
          <div className="auth-footer-text">
            <AuthFooterLink href="/signin">Back to sign in</AuthFooterLink>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard>
        <AuthHeader
          title="Set your password"
          subtitle={
            invite
              ? `Join ${invite.workspaceName} as ${invite.workspaceRole === 'workspace_admin' ? 'a workspace admin' : 'a workspace member'}.`
              : 'Complete your invitation.'
          }
        />

        <div className="auth-form-container">
          <div className="auth-invite-summary">
            Sign in email: <span className="auth-invite-summary__email">{email}</span>
            {invite?.moduleKeys.length ? (
              <span className="auth-invite-summary__modules">
                {' '}
                • Modules: {invite.moduleKeys.join(', ')}
              </span>
            ) : null}
          </div>

          {showGoogleAuth && (
            <>
              <GoogleAuthButton
                onClick={() => {
                  void handleGoogleSignIn();
                }}
                isLoading={isGoogleLoading}
              >
                Continue with Google
              </GoogleAuthButton>
              <AuthDivider />
            </>
          )}

          {generalError && <AuthMessage variant="error">{generalError}</AuthMessage>}

          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            <AuthPasswordInput
              name="password"
              label="Password"
              placeholder="Create a password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={errors.password}
              autoComplete="new-password"
              disabled={isSubmitting}
            />

            <AuthPasswordInput
              name="confirmPassword"
              label="Confirm password"
              placeholder="Repeat your password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={errors.confirmPassword}
              autoComplete="new-password"
              disabled={isSubmitting}
            />

            <AuthSubmitButton isLoading={isSubmitting}>Set password</AuthSubmitButton>
          </form>
        </div>

        <div className="auth-footer-text">
          Already have access? <AuthFooterLink href="/signin">Sign in</AuthFooterLink>
        </div>
      </AuthCard>
    </AuthLayout>
  );
}
